import { BASE, axiosCall, MethodType } from "./base";

const CONTENT_BASE = `${BASE}content/`;
const CONTENT_ENTRY = `${CONTENT_BASE}v1/entry`;
const CONTENT_ENTRIES = `${CONTENT_BASE}v1/entries`;

/**
 * Performs a preflight check and prepares the payload for API calls.
 *
 * @param {Object} payload - The payload for the API request.
 * @returns {Object|undefined} - The modified payload or undefined if preflight fails.
 */
function preflight(payload = {}) {
  if (!BASE) {
    console.error("[API] Base API URL is not set.");
    return;
  }

  if (!import.meta.env.VITE_CONTENT_API_KEY) {
    console.error("[API] Content API Key is not set.");
    return;
  }

  //payload.api_key = import.meta.env.VITE_CONTENT_API_KEY;
  payload.use_cache =
    import.meta.env.NODE_ENV === "production" ? "true" : "false";
  return payload;
}

/**
 * Fetches content entries by making an API call.
 *
 * @param {string} page - The page to fetch.
 * @returns {Promise} A promise that resolves with the API response or rejects if the preflight check fails.
 */
function readContentEntries(
  page = "Sheet1",
  api_key = import.meta.env.VITE_CONTENT_API_KEY
) {
  const payload = preflight({ page, api_key });

  if (!payload) {
    console.error("[API] Read Content Entries could not run.");
    return Promise.reject();
  }

  const params = new URLSearchParams(payload).toString();
  const url = `${CONTENT_ENTRIES}?${params}`;
  return axiosCall(MethodType.GET, url, payload);
}

/**
 * Creates a new content entry.
 *
 * @param {Object} data - The data for the new content entry.
 * @returns {Promise} A promise that resolves with the API response or rejects if the preflight check fails.
 */
function createContentEntry(data) {
  const payload = preflight();

  if (!payload) {
    console.error("[API] Create Content Entry could not run.");
    return Promise.reject(new Error("Preflight failed"));
  }

  payload.api_key = import.meta.env.VITE_CONTENT_API_KEY;
  payload.page = "Sheet1";
  payload.payload = JSON.stringify({ ...data });

  return axiosCall(MethodType.POST, CONTENT_ENTRY, payload)
    .then((response) => {
      console.log("Create Content Entry Response:", response);
      return response;
    })
    .catch((error) => {
      console.error(
        "Error in Create Content Entry:",
        error.response?.data || error
      );
      throw error;
    });
}

/**
 * Updates an existing content entry.
 *
 * @param {Object} data - The data for the content entry update.
 * @returns {Promise} A promise that resolves with the API response or rejects if the preflight check fails.
 */
function updateContentEntry(data) {
  const payload = preflight();

  if (!payload) {
    console.error("[API] Update Content Entry could not run.");
    return Promise.reject(new Error("Preflight failed"));
  }

  //
  // Sheet identification
  //
  payload.api_key = import.meta.env.VITE_CONTENT_API_KEY;
  payload.page = "Sheet1";

  //
  // Column (id, and value) to match against
  //
  payload.uid_key = "id";
  payload.uid_value = data.id;

  //
  // Payload, values in the row to replace.
  //
  payload.payload = JSON.stringify({
    id: data.id,
    value: data.value
  });

  return axiosCall(MethodType.PUT, CONTENT_ENTRY, payload)
    .then((response) => {
      console.log("Update Content Entry Response:", response);
      return response;
    })
    .catch((error) => {
      console.error(
        "Error in Update Content Entry:",
        error.response?.data || error
      );
      throw error;
    });
}

/**
 * Deletes a content entry by its unique identifier.
 *
 * @param {string} uidKey - The key of the unique identifier.
 * @param {string} uidValue - The value of the unique identifier.
 * @param {string} page - The page from which the entry should be deleted.
 * @returns {Promise} A promise that resolves with the API response or rejects if the preflight check fails.
 */
function deleteContentEntry(uidKey, uidValue, page = "Sheet1") {
  const payload = preflight();

  if (!payload) {
    console.error("[API] Delete Content Entry could not run.");
    return Promise.reject(new Error("Preflight failed"));
  }

  const params = new URLSearchParams({
    uid_key: uidKey,
    uid_value: uidValue,
    id: import.meta.env.VITE_CONTENT_API_KEY,
    page
  });

  const url = `${CONTENT_ENTRY}?${params.toString()}`;

  return axiosCall(MethodType.DELETE, url)
    .then((response) => {
      console.log("Delete Content Entry Response:", response);
      return response;
    })
    .catch((error) => {
      console.error(
        "Error in Delete Content Entry:",
        error.response?.data || error
      );
      throw error;
    });
}

export {
  readContentEntries,
  createContentEntry,
  updateContentEntry,
  deleteContentEntry
};
